import React, {useEffect, useId, useRef} from 'react';
import {Animated, Easing, View} from 'react-native';
import Svg, {Circle, ClipPath, Defs, Ellipse, G, Path, Rect} from 'react-native-svg';

import {BOTTLE_GEOMETRY, BottleVariant} from './bottleGeometry';

/**
 * A single coffee card bottle that can fill with liquid.
 *
 * Two styles, `wine` and `beer`, drawn by the same code from the two entries
 * in ./bottleGeometry. The only difference between them is data: their paths,
 * their resting liquid level, whether they carry a foam crown, and how their
 * bubbles behave. A beer
 * bottle pours a head and fizzes; a wine bottle does neither, because its
 * crown height is zero and its bubbles are slow.
 *
 * The outline is a shipped asset in `app/images/svg/rewards/`. Its inner
 * contour becomes the clip region (see ./bottleGeometry), so the liquid, its
 * surface and its bubbles are all confined to the inside of the glass, and the
 * outline is drawn last so the rim stays crisp over the top of them. Clipping
 * against the outline's own inner edge is what keeps the liquid off the wall:
 * the separate filled asset traces the outer body and overflows it.
 *
 * The curved surface is two ellipses riding the liquid level. Each one bulges
 * above the flat top of the liquid rectangle, so the two together read as a
 * meniscus. At rest they sit at 0.3 and 0.7 of the width; idle motion slides
 * them across and floats them up and down, which is what makes the liquid read
 * as sloshing rather than as a level that changed. This is deliberate:
 * `Animated` cannot interpolate a path `d`, so a literal wave path would have
 * to be driven some other way. Every animated attribute here is a plain
 * number.
 *
 * SHORTCUT: animates through React Native's `Animated` with
 * `useNativeDriver: false`, not Reanimated. Ceiling: every frame crosses the
 * JS bridge, so a long row on a busy screen can drop frames on a low-end
 * Android device. Chosen because this prototype's purpose is to be viewed on
 * web, and this repo already carries a patch for a Reanimated 4.5 web crash in
 * `_updatePropsJS`; layering animated SVG attributes on top of that is a
 * second risk surface for no prototype benefit. Upgrade path: swap the
 * `Animated.Value`s for `useSharedValue` and the animated primitives for
 * `useAnimatedProps`, which react-native-svg supports directly — the geometry,
 * the clip and the timings below all carry over unchanged.
 */

const AnimatedRect = Animated.createAnimatedComponent(Rect);
const AnimatedEllipse = Animated.createAnimatedComponent(Ellipse);
const AnimatedCircle = Animated.createAnimatedComponent(Circle);

/** One bottle's entry fill. The brief asks for 600-700ms. */
export const FILL_DURATION_MS = 650;

/**
 * Idle motion, once a bottle is full. Slow enough to read as liquid, not a
 * spinner.
 *
 * The two periods are deliberately not equal and not multiples of each other,
 * so the rise and the slide drift in and out of step and the loop does not
 * announce itself.
 */
const SURFACE_WOBBLE_MS = 2200;
const SURFACE_SWAY_MS = 3400;
const BUBBLE_TRAVEL_MS = 2600;

/**
 * The surface is sized as a share of the bottle's own width, not in fixed
 * viewBox units: the two styles are not the same size, so a fixed crest would
 * be half a wine bottle's wave and a whole beer bottle's.
 *
 * The fractions are the wine bottle's shipped numbers over its 104.37 width —
 * a crest 8 units tall lifting 6 — so wine keeps the size it shipped with.
 */
const SURFACE_CREST_RATIO = 8 / 104.37;
const SURFACE_WOBBLE_RATIO = 6 / 104.37;

/**
 * How far the crests slide to each side, as a share of the bottle's width.
 *
 * Capped by the wall, not by taste: a crest spans 0.44 of the width and sits
 * at 0.3 or 0.7 of it, so at more than 0.12 the leading crest pulls away from
 * the far wall and a notch of the flat rectangle top shows through at the
 * edge. 0.115 keeps both walls covered at either extreme.
 */
const SURFACE_SWAY_RATIO = 0.115;

/** Share of the fill budget spent climbing; the remainder is the settle. */
const SETTLE_SPLIT = 0.84;

/**
 * How far the liquid rises past its resting level before settling back, as a
 * share of the bottle's height so the overshoot reads the same on either style.
 */
const SETTLE_OVERSHOOT_RATIO = 9 / 425.2;

/**
 * Keeps a bubble from spawning exactly on the base of the glass, as a share of
 * the bottle's height for the same reason the crest is a share of its width.
 */
const BUBBLE_SPAWN_MARGIN_RATIO = 20 / 425.2;

/**
 * Bubbles carry both a light fill and a dark rim.
 *
 * Neither alone survives every brand. Measured against the shipped palettes:
 * SipnSave's primary is #FFDD00, so a white bubble on yellow liquid is close
 * to invisible; Bottlemart's is #0054A6, where a dark rim alone would be lost.
 * Drawing both means one of the two always carries the shape.
 */
const BUBBLE_FILL = '#FFFFFF';
const BUBBLE_RIM = 'rgba(0, 0, 0, 0.22)';
const BUBBLE_RIM_WIDTH = 1.2;
const BUBBLE_PEAK_OPACITY = 0.5;

type Bubble = {
  /** Horizontal position in viewBox units. */
  cx: number;
  radius: number;
  durationMs: number;
  delayMs: number;
};

/**
 * Fixed, hand-placed bubbles rather than random ones: a random layout changes
 * on every render, which makes a visual regression impossible to eyeball.
 *
 * Wine gets a few large, slow bubbles. Beer gets carbonation: nearly twice as
 * many, smaller and at half the travel time, which is what separates the two
 * at a glance once both are full.
 *
 * Beer's radii are about 0.75 of wine's rather than the quarter that would be
 * literal carbonation. A bubble that small measures under half a pixel at the
 * shipped bottle height, which is nothing at all.
 */
const BUBBLES: Record<BottleVariant, Bubble[]> = {
  wine: [
    {cx: 34, radius: 3.4, durationMs: BUBBLE_TRAVEL_MS, delayMs: 0},
    {cx: 58, radius: 2.4, durationMs: Math.round(BUBBLE_TRAVEL_MS * 0.78), delayMs: 420},
    {cx: 46, radius: 4.2, durationMs: Math.round(BUBBLE_TRAVEL_MS * 1.18), delayMs: 900},
    {cx: 68, radius: 2.8, durationMs: Math.round(BUBBLE_TRAVEL_MS * 0.9), delayMs: 1500},
  ],
  beer: [
    {cx: 30, radius: 2.6, durationMs: 1300, delayMs: 0},
    {cx: 48, radius: 2.0, durationMs: 1050, delayMs: 260},
    {cx: 66, radius: 3.0, durationMs: 1550, delayMs: 520},
    {cx: 82, radius: 2.3, durationMs: 1180, delayMs: 140},
    {cx: 38, radius: 1.9, durationMs: 950, delayMs: 780},
    {cx: 74, radius: 2.2, durationMs: 1400, delayMs: 960},
    {cx: 56, radius: 2.8, durationMs: 1250, delayMs: 1450},
  ],
};

/**
 * The foam crown, for a style whose `crownHeight` is non-zero.
 *
 * A pale cream. The head it has to imitate measures about #FDF8CD in the
 * reference silhouette - near-white with a yellow cast.
 *
 * The first attempt, #E6CEA0, was picked to survive SipnSave's #FFDD00 and was
 * too dark: on yellow it read as a brown band across the bottle. This one is
 * two thirds of the way back to the reference. Luminance against what it has
 * to separate from: 31 below SipnSave's #FFDD00, 17 below the white air above
 * it, and far above Bottlemart's #0054A6.
 */
const FOAM_FILL = '#FAF0C0';

/**
 * The inside face of the glass, painted before anything else so a bottle is a
 * bottle and not a window.
 *
 * Without it the air above the liquid is whatever is behind the component. On
 * the coffee card that is a white card and nothing shows; on any other
 * surface - the Storybook canvas is #F9FAFB - the empty neck and shoulder fill
 * with that colour and the bottle reads as having a grey background inside it.
 */
const GLASS_FILL = '#FFFFFF';

export type AnimatedBottleProps = {
  /** Which bottle to draw. */
  variant?: BottleVariant;
  /** Rendered height in px. Width follows the asset's aspect ratio. */
  height: number;
  /** Liquid and outline colour. */
  color: string;
  /**
   * Whether this bottle holds liquid at rest. An unfilled bottle renders the
   * outline only and never animates.
   */
  filled: boolean;
  /**
   * Run the entry fill. When false a filled bottle appears already full, with
   * no rise — this is the "already played, skip it" path.
   */
  animateEntry?: boolean;
  fillDurationMs?: number;
  /** Idle slosh and bubbles after the fill settles. */
  idleMotion?: boolean;
  onFillComplete?: () => void;
  testID?: string;
};

const AnimatedBottle = ({
  variant = 'wine',
  height,
  color,
  filled,
  animateEntry = false,
  fillDurationMs = FILL_DURATION_MS,
  idleMotion = true,
  onFillComplete,
  testID,
}: AnimatedBottleProps) => {
  const {viewBoxWidth, viewBoxHeight, floorY, fullY, crownHeight, outlinePath, interiorPath} =
    BOTTLE_GEOMETRY[variant];
  const width = Math.round(height * (viewBoxWidth / viewBoxHeight));

  // Sized from the bottle's own width, so a style that is not as wide as the
  // wine bottle gets a surface in proportion rather than the same absolute one.
  const crestRadiusY = viewBoxWidth * SURFACE_CREST_RATIO;
  const wobbleAmplitude = viewBoxWidth * SURFACE_WOBBLE_RATIO;
  const swayAmplitude = viewBoxWidth * SURFACE_SWAY_RATIO;

  // Where the liquid sits before it is poured. The crown rides `crownHeight`
  // above the liquid, so starting the surface at `floorY` alone would put the
  // crown ON the floor and open the pour with a band of foam already sitting
  // in the base. For a style with no crown this is `floorY` exactly.
  const emptyY = floorY + crownHeight;

  // useId keeps the clipPath id unique per instance. A shared id would make
  // every bottle in a row clip against whichever one mounted last.
  const clipId = `bottle-liquid-${useId()}`;

  const restingY = filled ? fullY : emptyY;
  const surfaceY = useRef(new Animated.Value(filled && animateEntry ? emptyY : restingY)).current;
  const wobble = useRef(new Animated.Value(0)).current;
  const sway = useRef(new Animated.Value(0.5)).current;

  // Held in a ref so a caller passing an inline arrow does not restart the
  // fill on every render.
  const onFillCompleteRef = useRef(onFillComplete);
  onFillCompleteRef.current = onFillComplete;

  // Entry fill.
  useEffect(() => {
    if (!filled) {
      surfaceY.setValue(emptyY);
      return;
    }

    if (!animateEntry) {
      surfaceY.setValue(fullY);
      onFillCompleteRef.current?.();
      return;
    }

    surfaceY.setValue(emptyY);

    // Pouring, not switching state. The climb takes most of the budget at a
    // near-even rate so the liquid is visibly rising the whole way, then
    // overshoots slightly and drops back — the settle is what makes it read as
    // liquid coming to rest rather than a bar reaching 100%.
    //
    // An ease-out curve was the first attempt and was wrong: it puts most of
    // the travel in the opening ~150ms, so at this duration the bottle looks
    // like it simply switched from empty to full.
    const climbMs = Math.round(fillDurationMs * SETTLE_SPLIT);
    const settleMs = fillDurationMs - climbMs;

    const rise = Animated.sequence([
      Animated.timing(surfaceY, {
        toValue: fullY - viewBoxHeight * SETTLE_OVERSHOOT_RATIO,
        duration: climbMs,
        easing: Easing.inOut(Easing.sin),
        useNativeDriver: false,
      }),
      Animated.timing(surfaceY, {
        toValue: fullY,
        duration: settleMs,
        easing: Easing.out(Easing.quad),
        useNativeDriver: false,
      }),
    ]);

    rise.start(({finished}) => {
      if (finished) {
        onFillCompleteRef.current?.();
      }
    });

    return () => rise.stop();
  }, [filled, animateEntry, fillDurationMs, surfaceY, emptyY, fullY, viewBoxHeight]);

  // Idle surface motion, looping for as long as the bottle holds liquid: the
  // surface floats up and down while the crests slide left, right and back.
  useEffect(() => {
    if (!filled || !idleMotion) {
      wobble.setValue(0);
      sway.setValue(0.5);
      return;
    }

    const wave = Animated.loop(
      Animated.sequence([
        Animated.timing(wobble, {
          toValue: 1,
          duration: SURFACE_WOBBLE_MS / 2,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: false,
        }),
        Animated.timing(wobble, {
          toValue: 0,
          duration: SURFACE_WOBBLE_MS / 2,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: false,
        }),
      ]),
    );

    // Out and back on one value, so the slide reverses at the same speed it
    // arrived with instead of snapping to the far side to start again.
    const slide = Animated.loop(
      Animated.sequence([
        Animated.timing(sway, {
          toValue: 1,
          duration: SURFACE_SWAY_MS / 2,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: false,
        }),
        Animated.timing(sway, {
          toValue: 0,
          duration: SURFACE_SWAY_MS,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: false,
        }),
        Animated.timing(sway, {
          toValue: 0.5,
          duration: SURFACE_SWAY_MS / 2,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: false,
        }),
      ]),
    );

    wave.start();
    slide.start();
    return () => {
      wave.stop();
      slide.stop();
    };
  }, [filled, idleMotion, wobble, sway]);

  // Both crests lift together, so the surface stays level and its bulge
  // breathes against the flat top of the liquid rectangle.
  //
  // They ran in opposite phase first, which tilted the surface. On a wine
  // bottle that is invisible, because a crest is the same colour as the liquid
  // under it. Under a foam crown it is not: the tilt showed as a wedge of
  // cream running down one wall, well below the head on the other side, which
  // reads as a rendering fault rather than as a slosh.
  const lift = wobble.interpolate({
    inputRange: [0, 1],
    outputRange: [-wobbleAmplitude, wobbleAmplitude],
  });

  // The crests slide together, so the bulge travels across the surface rather
  // than the two of them pumping against each other.
  const slideX = sway.interpolate({
    inputRange: [0, 1],
    outputRange: [-swayAmplitude, swayAmplitude],
  });

  const leftCrestX = Animated.add(new Animated.Value(viewBoxWidth * 0.3), slideX);
  const rightCrestX = Animated.add(new Animated.Value(viewBoxWidth * 0.7), slideX);

  const crestSurfaceY = Animated.add(surfaceY, lift);

  const bubblesActive = filled && idleMotion;

  // The crown rides the liquid, `crownHeight` above it and in the same phase,
  // so the head keeps its thickness while the surface moves.
  const crownY = Animated.subtract(surfaceY, crownHeight);
  const crestCrownY = Animated.add(crownY, lift);

  return (
    <View testID={testID}>
      <Svg width={width} height={height} viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}>
        <Defs>
          <ClipPath id={clipId}>
            <Path d={interiorPath} />
          </ClipPath>
        </Defs>

        {/* The inside of the glass, under everything. */}
        <Path d={interiorPath} fill={GLASS_FILL} />

        {/* An empty bottle draws no liquid layer at all. The meniscus ellipses
            are centred ON the surface, so half of each one sits above it; with
            the surface resting on the floor that half showed as a lens of
            liquid pooled inside the base of a bottle the member has not earned,
            thickening its bottom edge. */}
        {filled ? (
          <G clipPath={`url(#${clipId})`}>
            {/* The crown is a second surface, drawn the same way as the liquid
                and stacked under it. The liquid's own crests then paint over
                the flat underside of the foam, so the line where head meets
                beer is the liquid's meniscus rather than a ruled edge. */}
            {crownHeight > 0 ? (
              <>
                <AnimatedRect
                  x={0}
                  y={crownY}
                  width={viewBoxWidth}
                  height={viewBoxHeight}
                  fill={FOAM_FILL}
                />
                <AnimatedEllipse
                  cx={leftCrestX}
                  cy={crestCrownY}
                  rx={viewBoxWidth * 0.44}
                  ry={crestRadiusY}
                  fill={FOAM_FILL}
                />
                <AnimatedEllipse
                  cx={rightCrestX}
                  cy={crestCrownY}
                  rx={viewBoxWidth * 0.44}
                  ry={crestRadiusY}
                  fill={FOAM_FILL}
                />
              </>
            ) : null}

            <AnimatedRect
              x={0}
              y={surfaceY}
              width={viewBoxWidth}
              height={viewBoxHeight}
              fill={color}
            />

            {/* Opaque liquid colour, not a tint: the half of each ellipse that
              sits above the rectangle IS the crest, so it has to match the
              body exactly or the surface shows a seam. */}
            <AnimatedEllipse
              cx={leftCrestX}
              cy={crestSurfaceY}
              rx={viewBoxWidth * 0.44}
              ry={crestRadiusY}
              fill={color}
            />
            <AnimatedEllipse
              cx={rightCrestX}
              cy={crestSurfaceY}
              rx={viewBoxWidth * 0.44}
              ry={crestRadiusY}
              fill={color}
            />

            {BUBBLES[variant].map((bubble, index) => (
              <BubbleCircle
                key={index}
                bubble={bubble}
                surfaceY={surfaceY}
                depth={floorY - fullY - viewBoxHeight * BUBBLE_SPAWN_MARGIN_RATIO}
                active={bubblesActive}
              />
            ))}
          </G>
        ) : null}

        {/* Outline last, so the rim stays crisp over the liquid. */}
        <Path d={outlinePath} fill={color} />
      </Svg>
    </View>
  );
};

/**
 * One rising bubble.
 *
 * Its own component so each bubble owns its loop and phase offset without
 * hooks being called in a loop. Its height is expressed as a distance below
 * the live liquid surface, so a bubble tracks the surface while the bottle is
 * still filling instead of escaping above it.
 */
const BubbleCircle = ({
  bubble,
  surfaceY,
  depth: travel,
  active,
}: {
  bubble: Bubble;
  surfaceY: Animated.Value;
  /** How far below the surface a bubble starts, in viewBox units. */
  depth: number;
  active: boolean;
}) => {
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!active) {
      progress.setValue(0);
      return;
    }

    const rise = Animated.loop(
      Animated.timing(progress, {
        toValue: 1,
        duration: bubble.durationMs,
        delay: bubble.delayMs,
        easing: Easing.linear,
        useNativeDriver: false,
      }),
    );

    rise.start();
    return () => rise.stop();
  }, [active, bubble.durationMs, bubble.delayMs, progress]);

  if (!active) {
    return null;
  }

  // Depth below the surface: full at progress 0, zero at progress 1.
  const depth = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [travel, 0],
  });

  const opacity = progress.interpolate({
    inputRange: [0, 0.15, 0.75, 1],
    outputRange: [0, BUBBLE_PEAK_OPACITY, BUBBLE_PEAK_OPACITY, 0],
  });

  return (
    <AnimatedCircle
      cx={bubble.cx}
      cy={Animated.add(surfaceY, depth)}
      r={bubble.radius}
      fill={BUBBLE_FILL}
      stroke={BUBBLE_RIM}
      strokeWidth={BUBBLE_RIM_WIDTH}
      opacity={opacity}
    />
  );
};

export default AnimatedBottle;
